/**
 * =============================================================================
 * IDS CRM Web - Global Search palette (Batch E)
 * =============================================================================
 *
 * Ctrl+K (or the sidebar magnifier) opens a search overlay across customers,
 * contacts, assets, tickets and opportunities. Enter/click navigates.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './GlobalSearch.css';

const GROUPS = [
  { key: 'customers', label: 'Customers', path: (r) => `/customers/${r.customer_id}`, primary: (r) => r.business_name, secondary: (r) => `${r.customer_number || ''} · ${r.customer_type || ''}` },
  { key: 'contacts', label: 'Contacts', path: (r) => `/customers/${r.customer_id}?tab=contacts`, primary: (r) => r.full_name, secondary: (r) => `${r.customer_name || ''}${r.email ? ` · ${r.email}` : ''}` },
  { key: 'assets', label: 'Assets', path: (r) => `/assets/${r.asset_id}`, primary: (r) => r.asset_ref, secondary: (r) => `${String(r.robot_type || '').replace(/_/g, ' ')} · ${r.customer_name || ''}${r.serial_number ? ` · ${r.serial_number}` : ''}` },
  { key: 'tickets', label: 'Tickets', path: (r) => `/tickets/${r.ticket_id}`, primary: (r) => `${r.ticket_ref} — ${r.subject}`, secondary: (r) => `${String(r.status || '').replace(/_/g, ' ')} · ${r.customer_name || ''}` },
  { key: 'opportunity_groups', label: 'Opportunities', path: (r) => `/pipeline/groups/${r.group_id}`, primary: (r) => `${r.group_ref} — ${r.title}`, secondary: (r) => `${r.status || ''} · ${r.customer_name || ''}` },
  // Line items headline under their group's name (never a bare OPP ref) and
  // land on the group page — the line detail is one click from there.
  {
    key: 'line_items',
    label: 'Line Items',
    path: (r) => (r.opportunity_group_id ? `/pipeline/groups/${r.opportunity_group_id}` : `/pipeline/${r.opportunity_id}`),
    primary: (r) => `${r.group_ref || r.opp_ref} · ${String(r.product || '').replace(/_/g, ' ') || r.description || 'non-product'}`,
    secondary: (r) => `${r.stage || 'no pipeline'} · ${r.customer_name || ''}`
  }
];

function GlobalSearch({ open, onClose }) {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTerm('');
      setResults(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open || term.trim().length < 2) {
      setResults(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await authFetch(`/api/v1/search?q=${encodeURIComponent(term.trim())}`);
        if (response.ok && !cancelled) {
          setResults(await response.json());
        }
      } catch (err) {
        console.error('Global search failed:', err);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [term, open, authFetch]);

  const go = useCallback((path) => {
    onClose();
    navigate(path);
  }, [navigate, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const total = results ? GROUPS.reduce((sum, group) => sum + (results[group.key]?.length || 0), 0) : 0;

  return (
    <div className="global-search-overlay" onClick={onClose}>
      <div className="global-search-panel" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search customers, contacts, assets, tickets, deals..."
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        <div className="global-search-results">
          {searching && <div className="global-search-hint">Searching...</div>}
          {!searching && term.trim().length >= 2 && results && total === 0 && (
            <div className="global-search-hint">No matches for "{term.trim()}"</div>
          )}
          {!searching && term.trim().length < 2 && (
            <div className="global-search-hint">Type at least 2 characters · Esc to close</div>
          )}
          {results && GROUPS.map(group => {
            const rows = results[group.key] || [];
            if (rows.length === 0) return null;
            return (
              <div key={group.key} className="global-search-group">
                <div className="global-search-group-label">{group.label}</div>
                {rows.map((row, index) => (
                  <button
                    key={index}
                    type="button"
                    className="global-search-item"
                    onClick={() => go(group.path(row))}
                  >
                    <strong>{group.primary(row)}</strong>
                    <span>{group.secondary(row)}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default GlobalSearch;
